import React, { useState, useEffect, useCallback, useRef } from 'react';
import { axiosInstance } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Truck, Package, Camera, Upload, X, Image, CheckCircle2, Clock, User, CreditCard, Calendar, Search, ChevronDown, ChevronUp } from 'lucide-react';
import VisualTimeline from './VisualTimeline';

const formatINR = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
};

function getSlaCountdown(assignment) {
  if (!assignment.sla_deadline) return null;
  const now = new Date();
  const deadline = new Date(assignment.sla_deadline);
  const diffMs = deadline - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return { overdue: true, text: `OVERDUE by ${Math.abs(diffDays)}d` };
  }
  return { overdue: false, text: `${diffDays}d left` };
}

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

function validatePhoto(file) {
  if (!file) return 'Photo is required';
  if (!ALLOWED_TYPES.includes(file.type)) return 'Only JPEG, PNG, or WebP images allowed';
  if (file.size > MAX_FILE_SIZE) return 'Photo must be under 10MB';
  return null;
}

export default function DeliveryTab({ user }) {
  const [activeQueue, setActiveQueue] = useState('ops');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Delivery/Pickup dialog
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, assignment: null });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Timeline dialog
  const [showTimeline, setShowTimeline] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  // Member lookup state
  const [memberData, setMemberData] = useState({}); // keyed by assignment id
  const [lookingUp, setLookingUp] = useState({}); // keyed by assignment id
  const [expandedCards, setExpandedCards] = useState({}); // keyed by assignment id

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both queues worth of data
      const statuses = ['admin_approved', 'ops_delivered', 'customer_received'];
      const res = await axiosInstance.get('/supplements/assignments', {
        params: { status_in: statuses.join(',') },
      });
      setAssignments(res.data?.assignments || []);
    } catch (error) {
      toast.error('Failed to load delivery queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const lookupMember = async (assignment) => {
    if (!assignment.customer_phone) return;
    setLookingUp(prev => ({ ...prev, [assignment.id]: true }));
    try {
      const params = { phone: assignment.customer_phone.replace(/\D/g, '').slice(-10), include_all: true };
      const res = await axiosInstance.get('/supplements/lookup-member', { params });
      // Prefer members (all subs) over annual_members for delivery context
      const members = res.data?.members || res.data?.annual_members || [];
      if (members.length > 0) {
        setMemberData(prev => ({ ...prev, [assignment.id]: members[0] }));
      } else {
        setMemberData(prev => ({ ...prev, [assignment.id]: { not_found: true, reason: res.data?.reason || 'No subscription found for this phone' } }));
      }
    } catch {
      setMemberData(prev => ({ ...prev, [assignment.id]: { not_found: true, reason: 'Lookup failed — WTF API may be unavailable' } }));
    } finally {
      setLookingUp(prev => ({ ...prev, [assignment.id]: false }));
    }
  };

  const toggleExpand = (id, e) => {
    e.stopPropagation();
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Filter assignments by queue
  const opsQueue = assignments.filter(a => a.status === 'admin_approved');
  const opsDelivered = assignments.filter(a => a.status === 'ops_delivered');
  const pickupQueue = assignments.filter(a => a.status === 'ops_delivered');
  const completed = assignments.filter(a => a.status === 'customer_received');

  const openActionDialog = (type, assignment) => {
    setActionDialog({ open: true, type, assignment });
    setPhotoFile(null);
    setPhotoPreview(null);
    setNotes('');
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validatePhoto(file);
    if (error) {
      toast.error(error);
      return;
    }
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmitAction = async () => {
    const { type, assignment } = actionDialog;
    if (!assignment) return;

    if (!photoFile) {
      toast.error('Photo proof is required');
      return;
    }

    const photoError = validatePhoto(photoFile);
    if (photoError) {
      toast.error(photoError);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', photoFile, type === 'ops' ? 'delivery.jpg' : 'pickup.jpg');
      if (notes.trim()) formData.append('notes', notes.trim());

      const endpoint = type === 'ops'
        ? `/supplements/assignments/${assignment.id}/ops-deliver`
        : `/supplements/assignments/${assignment.id}/customer-pickup`;

      await axiosInstance.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success(type === 'ops' ? 'Delivery confirmed' : 'Pickup confirmed');
      setActionDialog({ open: false, type: null, assignment: null });
      fetchAssignments();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to confirm ${type === 'ops' ? 'delivery' : 'pickup'}`);
    } finally {
      setUploading(false);
    }
  };

  const openTimeline = (assignment) => {
    setSelectedAssignment(assignment);
    setShowTimeline(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Delivery Management</h2>
          <p className="text-sm text-muted-foreground">Track deliveries and customer pickups</p>
        </div>
      </div>

      {/* Queue Toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeQueue === 'ops' ? 'default' : 'outline'}
          onClick={() => setActiveQueue('ops')}
          data-testid="ops-queue-tab"
        >
          <Truck className="w-4 h-4 mr-2" />
          Ops Delivery ({opsQueue.length})
        </Button>
        <Button
          variant={activeQueue === 'pickup' ? 'default' : 'outline'}
          onClick={() => setActiveQueue('pickup')}
          data-testid="pickup-queue-tab"
        >
          <Package className="w-4 h-4 mr-2" />
          Customer Pickup ({pickupQueue.length})
        </Button>
      </div>

      {/* Ops Delivery Queue */}
      {activeQueue === 'ops' && (
        <div className="space-y-6">
          {/* Pending Deliveries */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending Delivery ({opsQueue.length})
            </h3>
            {opsQueue.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Truck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-muted-foreground">No pending deliveries</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {opsQueue.map(a => {
                  const sla = getSlaCountdown(a);
                  const expanded = expandedCards[a.id];
                  const member = memberData[a.id];
                  const hasMembership = a.membership_plan || a.membership_start;
                  return (
                    <Card
                      key={a.id}
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => openTimeline(a)}
                      data-testid={`ops-card-${a.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="font-medium text-slate-800">{a.customer_name}</div>
                          {sla && (
                            <Badge className={sla.overdue ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-green-100 text-green-700'}>
                              {sla.text}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-slate-500 space-y-0.5">
                          <div>{a.customer_phone}</div>
                          <div>Gym: {a.gym_name || '—'}</div>
                          <div>Stack: {a.stack_name || '—'}</div>
                        </div>

                        {/* Stack Items */}
                        {a.stack_items?.length > 0 && (
                          <div className="mt-2 bg-slate-50 rounded p-2">
                            <p className="text-[11px] font-medium text-slate-600 mb-1">Stack Contents:</p>
                            {a.stack_items.map((item, i) => (
                              <div key={i} className="text-[11px] text-slate-500 flex justify-between">
                                <span>{item.product_name}</span>
                                <span>{item.quantity} {item.unit}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Membership Details (from assignment or lookup) */}
                        {hasMembership ? (
                          <div className="mt-2 border border-blue-100 bg-blue-50 rounded p-2">
                            <p className="text-[11px] font-medium text-blue-700 mb-1 flex items-center gap-1">
                              <CreditCard className="w-3 h-3" /> Membership
                            </p>
                            <div className="text-[11px] text-blue-600 space-y-0.5">
                              {a.membership_plan && <div>Plan: {a.membership_plan}</div>}
                              <div className="flex gap-3">
                                {a.membership_start && <span>Start: {formatDate(a.membership_start)}</span>}
                                {a.membership_expiry && <span>Expiry: {formatDate(a.membership_expiry)}</span>}
                              </div>
                              {a.membership_price > 0 && <div>Price: {formatINR(a.membership_price)}</div>}
                            </div>
                          </div>
                        ) : member && !member.not_found ? (
                          <div className="mt-2 border border-blue-100 bg-blue-50 rounded p-2">
                            <p className="text-[11px] font-medium text-blue-700 mb-1 flex items-center gap-1">
                              <CreditCard className="w-3 h-3" /> Membership (via lookup)
                            </p>
                            <div className="text-[11px] text-blue-600 space-y-0.5">
                              {member.plan_name && <div>Plan: {member.plan_name}</div>}
                              <div className="flex gap-3">
                                {member.start_date && <span>Start: {formatDate(member.start_date)}</span>}
                                {member.expire_date && <span>Expiry: {formatDate(member.expire_date)}</span>}
                              </div>
                              {member.price > 0 && <div>Price: {formatINR(member.price)}</div>}
                            </div>
                          </div>
                        ) : member?.not_found ? (
                          <div className="mt-2 text-[11px] text-amber-600 bg-amber-50 rounded p-2">
                            {member.reason}
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-full mt-2 text-xs"
                            disabled={lookingUp[a.id]}
                            onClick={(e) => { e.stopPropagation(); lookupMember(a); }}
                          >
                            {lookingUp[a.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                            Fetch Member Info
                          </Button>
                        )}

                        <Button
                          className="w-full mt-3"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); openActionDialog('ops', a); }}
                          data-testid={`deliver-btn-${a.id}`}
                        >
                          <Truck className="w-4 h-4 mr-2" />
                          Deliver
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Already Delivered */}
          {opsDelivered.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Delivered ({opsDelivered.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {opsDelivered.map(a => (
                  <Card
                    key={a.id}
                    className="cursor-pointer hover:shadow-md transition-shadow opacity-75"
                    onClick={() => openTimeline(a)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-slate-800">{a.customer_name}</div>
                        <Badge className="bg-purple-100 text-purple-700">Delivered</Badge>
                      </div>
                      <div className="text-sm text-slate-500 space-y-0.5">
                        <div>{a.customer_phone}</div>
                        <div>Gym: {a.gym_name || '—'}</div>
                        <div>Stack: {a.stack_name || '—'}</div>
                      </div>
                      {a.ops_delivery_photo_url && (
                        <img
                          src={a.ops_delivery_photo_url}
                          alt="Delivery proof"
                          className="w-16 h-16 object-cover rounded mt-2 border"
                        />
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Customer Pickup Queue */}
      {activeQueue === 'pickup' && (
        <div className="space-y-6">
          {/* Pending Pickups */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Awaiting Pickup ({pickupQueue.length})
            </h3>
            {pickupQueue.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <Package className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-muted-foreground">No pending pickups</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pickupQueue.map(a => {
                  const member = memberData[a.id];
                  const hasMembership = a.membership_plan || a.membership_start;
                  return (
                  <Card
                    key={a.id}
                    className="cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => openTimeline(a)}
                    data-testid={`pickup-card-${a.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="font-medium text-slate-800 mb-2">{a.customer_name}</div>
                      <div className="text-sm text-slate-500 space-y-0.5">
                        <div>{a.customer_phone}</div>
                        <div>Gym: {a.gym_name || '—'}</div>
                        <div>Stack: {a.stack_name || '—'}</div>
                        <div>Delivered: {formatDate(a.ops_delivered_at)}</div>
                      </div>

                      {/* Stack Items */}
                      {a.stack_items?.length > 0 && (
                        <div className="mt-2 bg-slate-50 rounded p-2">
                          <p className="text-[11px] font-medium text-slate-600 mb-1">Stack Contents:</p>
                          {a.stack_items.map((item, i) => (
                            <div key={i} className="text-[11px] text-slate-500 flex justify-between">
                              <span>{item.product_name}</span>
                              <span>{item.quantity} {item.unit}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Membership Details */}
                      {hasMembership ? (
                        <div className="mt-2 border border-blue-100 bg-blue-50 rounded p-2">
                          <p className="text-[11px] font-medium text-blue-700 mb-1 flex items-center gap-1">
                            <CreditCard className="w-3 h-3" /> Membership
                          </p>
                          <div className="text-[11px] text-blue-600 space-y-0.5">
                            {a.membership_plan && <div>Plan: {a.membership_plan}</div>}
                            <div className="flex gap-3">
                              {a.membership_start && <span>Start: {formatDate(a.membership_start)}</span>}
                              {a.membership_expiry && <span>Expiry: {formatDate(a.membership_expiry)}</span>}
                            </div>
                            {a.membership_price > 0 && <div>Price: {formatINR(a.membership_price)}</div>}
                          </div>
                        </div>
                      ) : member && !member.not_found ? (
                        <div className="mt-2 border border-blue-100 bg-blue-50 rounded p-2">
                          <p className="text-[11px] font-medium text-blue-700 mb-1 flex items-center gap-1">
                            <CreditCard className="w-3 h-3" /> Membership (via lookup)
                          </p>
                          <div className="text-[11px] text-blue-600 space-y-0.5">
                            {member.plan_name && <div>Plan: {member.plan_name}</div>}
                            <div className="flex gap-3">
                              {member.start_date && <span>Start: {formatDate(member.start_date)}</span>}
                              {member.expire_date && <span>Expiry: {formatDate(member.expire_date)}</span>}
                            </div>
                            {member.price > 0 && <div>Price: {formatINR(member.price)}</div>}
                          </div>
                        </div>
                      ) : member?.not_found ? (
                        <div className="mt-2 text-[11px] text-amber-600 bg-amber-50 rounded p-2">
                          {member.reason}
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full mt-2 text-xs"
                          disabled={lookingUp[a.id]}
                          onClick={(e) => { e.stopPropagation(); lookupMember(a); }}
                        >
                          {lookingUp[a.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                          Fetch Member Info
                        </Button>
                      )}

                      <Button
                        className="w-full mt-3"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openActionDialog('pickup', a); }}
                        data-testid={`pickup-btn-${a.id}`}
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Record Pickup
                      </Button>
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Completed */}
          {completed.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                Completed ({completed.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completed.map(a => (
                  <Card
                    key={a.id}
                    className="cursor-pointer hover:shadow-md transition-shadow opacity-75"
                    onClick={() => openTimeline(a)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="font-medium text-slate-800">{a.customer_name}</div>
                        <Badge className="bg-green-100 text-green-700">Complete</Badge>
                      </div>
                      <div className="text-sm text-slate-500 space-y-0.5">
                        <div>{a.customer_phone}</div>
                        <div>Gym: {a.gym_name || '—'}</div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        {a.ops_delivery_photo_url && (
                          <div>
                            <p className="text-[10px] text-slate-400 mb-0.5">Delivery</p>
                            <img
                              src={a.ops_delivery_photo_url}
                              alt="Delivery proof"
                              className="w-14 h-14 object-cover rounded border"
                            />
                          </div>
                        )}
                        {a.customer_pickup_photo_url && (
                          <div>
                            <p className="text-[10px] text-slate-400 mb-0.5">Pickup</p>
                            <img
                              src={a.customer_pickup_photo_url}
                              alt="Pickup proof"
                              className="w-14 h-14 object-cover rounded border"
                            />
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delivery/Pickup Upload Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => {
        if (!open) {
          setActionDialog({ open: false, type: null, assignment: null });
          clearPhoto();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === 'ops' ? 'Confirm Delivery' : 'Confirm Customer Pickup'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-4">
            {actionDialog.assignment && (() => {
              const a = actionDialog.assignment;
              const member = memberData[a.id];
              const hasMembership = a.membership_plan || a.membership_start;
              const mData = hasMembership ? {
                plan: a.membership_plan, start: a.membership_start,
                expiry: a.membership_expiry, price: a.membership_price
              } : member && !member.not_found ? {
                plan: member.plan_name, start: member.start_date,
                expiry: member.expire_date, price: member.price
              } : null;
              return (
              <div className="p-3 bg-slate-50 rounded-lg text-sm space-y-2">
                <div className="font-medium">{a.customer_name}</div>
                <div className="text-slate-500">
                  {a.customer_phone} | {a.gym_name || '—'}
                </div>
                <div className="text-slate-500">Stack: {a.stack_name || '—'}</div>
                {a.stack_items?.length > 0 && (
                  <div className="border-t pt-1.5 mt-1.5">
                    <p className="text-xs font-medium text-slate-600 mb-1">Items:</p>
                    {a.stack_items.map((item, i) => (
                      <div key={i} className="text-xs text-slate-500 flex justify-between">
                        <span>{item.product_name}</span>
                        <span>{item.quantity} {item.unit}</span>
                      </div>
                    ))}
                  </div>
                )}
                {mData ? (
                  <div className="border-t pt-1.5 mt-1.5">
                    <p className="text-xs font-medium text-blue-700 flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> Membership
                    </p>
                    <div className="text-xs text-blue-600 space-y-0.5 mt-0.5">
                      {mData.plan && <div>Plan: {mData.plan}</div>}
                      <div className="flex gap-3">
                        {mData.start && <span>Start: {formatDate(mData.start)}</span>}
                        {mData.expiry && <span>Expiry: {formatDate(mData.expiry)}</span>}
                      </div>
                      {mData.price > 0 && <div>Price: {formatINR(mData.price)}</div>}
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full text-xs"
                    disabled={lookingUp[a.id]}
                    onClick={() => lookupMember(a)}
                  >
                    {lookingUp[a.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                    Fetch Member Info
                  </Button>
                )}
              </div>
              );
            })()}

            {/* Photo Upload */}
            <div className="space-y-2">
              <Label>Proof Photo *</Label>
              {photoPreview ? (
                <div className="relative inline-block">
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="w-full max-h-48 object-cover rounded-lg border"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-1 right-1 bg-white/80 hover:bg-white rounded-full w-6 h-6 p-0"
                    onClick={clearPhoto}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">Click to upload photo</p>
                  <p className="text-xs text-slate-400 mt-1">JPEG, PNG, or WebP (max 10MB)</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoSelect}
                className="hidden"
                data-testid="photo-input"
              />
              {!photoPreview && (
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2" />
                  Take/Choose Photo
                </Button>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label>Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setActionDialog({ open: false, type: null, assignment: null });
              clearPhoto();
            }}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAction}
              disabled={uploading || !photoFile}
              data-testid="confirm-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {uploading
                ? 'Uploading...'
                : actionDialog.type === 'ops'
                  ? 'Confirm Delivery'
                  : 'Confirm Pickup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline Dialog */}
      <Dialog open={showTimeline} onOpenChange={setShowTimeline}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Assignment Timeline</DialogTitle>
          </DialogHeader>
          {selectedAssignment && (() => {
            const a = selectedAssignment;
            const member = memberData[a.id];
            const hasMembership = a.membership_plan || a.membership_start;
            const mData = hasMembership ? {
              plan: a.membership_plan, start: a.membership_start,
              expiry: a.membership_expiry, price: a.membership_price
            } : member && !member.not_found ? {
              plan: member.plan_name, start: member.start_date,
              expiry: member.expire_date, price: member.price
            } : null;
            return (
            <div>
              <div className="mb-4 p-3 bg-slate-50 rounded-lg text-sm space-y-2">
                <div className="font-medium">{a.customer_name}</div>
                <div className="text-slate-500">
                  {a.customer_phone} | {a.gym_name || '—'} | {a.stack_name || '—'}
                </div>
                {a.stack_items?.length > 0 && (
                  <div className="border-t pt-1.5">
                    <p className="text-xs font-medium text-slate-600 mb-1">Stack Contents:</p>
                    {a.stack_items.map((item, i) => (
                      <div key={i} className="text-xs text-slate-500 flex justify-between">
                        <span>{item.product_name}</span>
                        <span>{item.quantity} {item.unit}</span>
                      </div>
                    ))}
                  </div>
                )}
                {mData ? (
                  <div className="border-t pt-1.5">
                    <p className="text-xs font-medium text-blue-700 flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> Membership
                    </p>
                    <div className="text-xs text-blue-600 space-y-0.5 mt-0.5">
                      {mData.plan && <div>Plan: {mData.plan}</div>}
                      <div className="flex gap-3">
                        {mData.start && <span>Start: {formatDate(mData.start)}</span>}
                        {mData.expiry && <span>Expiry: {formatDate(mData.expiry)}</span>}
                      </div>
                      {mData.price > 0 && <div>Price: {formatINR(mData.price)}</div>}
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={lookingUp[a.id]}
                    onClick={() => lookupMember(a)}
                  >
                    {lookingUp[a.id] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Search className="w-3 h-3 mr-1" />}
                    Fetch Member Info
                  </Button>
                )}
              </div>
              <VisualTimeline assignmentId={a.id} />
            </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
